import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, MessageSquare, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { appointmentService } from '../../services/appointmentService';
import { useLanguage } from '../../hooks/useLanguage';
import { SlotPicker } from './SlotPicker';
import { InspectionRecap } from './InspectionRecap';
import {
    Appointment, MAX_COUNTER, currentSlot, endTimeOf, errorCode, formatDay, formatMoney, styleOf,
    timeToMin, todayKey, vehicleLabel
} from './appointmentUtils';

interface DayLoadItem { id: string; timeSlot: string; endTime?: string; duration: number; status: string; clientName: string }
interface Suggestion { date: string; timeSlot: string; duration: number }
interface Props {
    appointment: Appointment;
    startInCounter?: boolean;
    lunch?: [string, string] | null;
    onClose: () => void;
    onChanged: () => void;
}

const HATCH = { backgroundImage: 'repeating-linear-gradient(135deg, #f1f5f9 0, #f1f5f9 5px, #e2e8f0 5px, #e2e8f0 6px)' };

// Fenêtre « Demande de rendez-vous » : créneau demandé, frise du jour, client, devis, préparation, autres créneaux
export const RequestModal: React.FC<Props> = ({ appointment: initial, startInCounter, lunch, onClose, onChanged }) => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const navigate = useNavigate();
    const [a, setA] = useState<Appointment>(initial);
    const [dayLoad, setDayLoad] = useState<DayLoadItem[]>([]);
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [picked, setPicked] = useState<Suggestion | null>(null);
    const [mode, setMode] = useState<'view' | 'counter' | 'refuse'>(startInCounter ? 'counter' : 'view');
    const [buffer, setBuffer] = useState(false);
    const [reason, setReason] = useState('');
    const [date, setDate] = useState(todayKey());
    const [timeSlot, setTimeSlot] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [d, s] = await Promise.all([appointmentService.getAppointmentById(initial._id), appointmentService.getSuggestions(initial._id, 3)]);
                if (cancelled) return;
                setA(d.data); setDayLoad(d.context?.dayLoad || []); setSuggestions(s.data?.suggestions || []);
            } catch { /* le détail de base suffit */ }
        })();
        return () => { cancelled = true; };
    }, [initial._id]);

    const slot = currentSlot(a);
    const reqStart = timeToMin(slot.timeSlot);
    const reqEnd = reqStart + slot.duration;
    const conflict = dayLoad.some(d => d.status !== 'COMPLETED' && timeToMin(d.timeSlot) < reqEnd && timeToMin(d.timeSlot) + d.duration > reqStart);
    const counterLeft = (a.counterCount || 0) < MAX_COUNTER;
    const items = a.quoteSnapshot?.items || [];

    // Frise du jour : du plus tôt au plus tard, marges comprises (8 h – 18 h au minimum)
    const range = useMemo(() => {
        const all = [reqStart, reqEnd, ...dayLoad.flatMap(d => [timeToMin(d.timeSlot), timeToMin(d.timeSlot) + d.duration])];
        return { s: Math.min(8 * 60, Math.floor(Math.min(...all) / 60) * 60), e: Math.max(18 * 60, Math.ceil(Math.max(...all) / 60) * 60) };
    }, [dayLoad, reqStart, reqEnd]);
    const pct = (m: number) => `${((m - range.s) / (range.e - range.s)) * 100}%`;
    const w = (from: number, to: number) => `${((to - from) / (range.e - range.s)) * 100}%`;
    const ticks: number[] = [];
    for (let m = range.s; m <= range.e; m += 120) ticks.push(m);

    const run = async (fn: () => Promise<any>) => {
        setBusy(true); setError('');
        try { await fn(); onChanged(); onClose(); }
        catch (err) { setError(t(`appointments.errors.${errorCode(err)}`, { defaultValue: t('appointments.errors.generic') })); }
        finally { setBusy(false); }
    };
    const confirm = () => run(() => appointmentService.respond(a._id, {
        action: 'ACCEPT',
        ...(buffer ? { bufferAfter: 30 } : {})
    }));
    const refuse = () => run(() => appointmentService.respond(a._id, { action: 'REFUSE', reason: reason || undefined }));
    const counter = () => {
        const target = picked || (timeSlot ? { date, timeSlot } : null);
        if (!target) { setMode('counter'); return; }
        return run(() => appointmentService.respond(a._id, { action: 'COUNTER', date: target.date, timeSlot: target.timeSlot }));
    };

    const card = 'rounded-2xl border border-gray-200 bg-white p-4';
    const dow = formatDay(slot.date, currentLanguage);

    return (
        <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 sm:p-4" onClick={onClose} role="dialog" aria-modal="true">
            <div className="bg-white w-full sm:max-w-4xl sm:rounded-2xl shadow-2xl max-h-screen sm:max-h-[92vh] overflow-y-auto flex flex-col" dir={isRTL ? 'rtl' : 'ltr'} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
                        <h2 className="text-lg font-bold text-gray-900">{t('appointments.request.title')}</h2>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${styleOf(a.status).badge}`}>{t(`appointments.status.${a.status}`)}</span>
                    </div>
                    <button onClick={onClose} aria-label={t('appointments.actions.close')} className="p-2 rounded-full hover:bg-gray-100"><X size={18} /></button>
                </div>

                <div className="p-4 sm:p-6 grid lg:grid-cols-5 gap-4 flex-1">
                    <div className="lg:col-span-3 space-y-4">
                        <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-4">
                            <p className="text-xs font-bold uppercase tracking-wide text-indigo-700">{t('appointments.request.requestedSlot')}</p>
                            <p className="text-xl sm:text-2xl font-extrabold text-gray-900 mt-1">{dow} · {slot.timeSlot} – {endTimeOf(slot)}</p>
                            <p className="text-sm text-gray-600 mt-1">
                                {a.serviceMode === 'WAIT' ? t('appointments.serviceMode.WAIT') : t('appointments.serviceMode.DROP_OFF')}
                                {a.serviceMode !== 'WAIT' ? ` · ${t('appointments.request.pickupAround', { time: endTimeOf(slot) })}` : ''}
                            </p>
                            <p className={`mt-2 text-sm font-semibold inline-flex items-center gap-1.5 ${conflict ? 'text-red-600' : 'text-green-700'}`}>
                                {conflict ? <AlertTriangle size={16} /> : <Check size={16} />}
                                {conflict ? t('appointments.request.conflict') : t('appointments.request.noConflict')}
                            </p>
                        </div>

                        <div className={card}>
                            <p className="text-sm font-bold text-gray-900 mb-3">{dow}</p>
                            <div className="relative h-9 rounded-lg bg-gray-50">
                                {lunch && <div className="absolute inset-y-0 rounded" style={{ left: pct(timeToMin(lunch[0])), width: w(timeToMin(lunch[0]), timeToMin(lunch[1])), ...HATCH }} />}
                                {dayLoad.map(d => (
                                    <div key={d.id} title={d.clientName} className="absolute inset-y-1 rounded bg-indigo-200" style={{ left: pct(timeToMin(d.timeSlot)), width: w(timeToMin(d.timeSlot), timeToMin(d.timeSlot) + d.duration) }} />
                                ))}
                                <div className="absolute inset-y-0.5 rounded border-2 border-dashed border-amber-500 bg-amber-100/80" style={{ left: pct(reqStart), width: w(reqStart, reqEnd) }} />
                            </div>
                            <div className="relative h-4 mt-1 text-[10px] text-gray-400">
                                {ticks.map(m => <span key={m} className="absolute -translate-x-1/2" style={{ left: pct(m) }}>{String(Math.floor(m / 60)).padStart(2, '0')}:00</span>)}
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className={card}>
                                <p className="text-xs text-gray-500">{t('appointments.request.client')}</p>
                                <p className="font-bold text-gray-900">{a.clientName}</p>
                                {a.inspectionId && <button onClick={() => navigate('/dashboard/messages')} className="mt-1 inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:underline"><MessageSquare size={14} />{t('appointments.detail.chat')}</button>}
                            </div>
                            <div className={card}>
                                <p className="text-xs text-gray-500">{t('appointments.request.vehicle')}</p>
                                <p className="font-bold text-gray-900">{vehicleLabel(a) || '—'}</p>
                                {a.vehiclePlateNumber && <p className="text-sm text-gray-500">{a.vehiclePlateNumber}</p>}
                            </div>
                        </div>

                        {(a.clientNote || a.description) && (
                            <div className={card}>
                                <p className="text-xs text-gray-500">{t('appointments.detail.note')}</p>
                                <p className="text-sm text-gray-800 mt-1">« {a.clientNote || a.description} »</p>
                            </div>
                        )}
                    </div>

                    <div className="lg:col-span-2 space-y-4">
                        <div className="rounded-2xl bg-indigo-50/70 border border-indigo-100 p-4">
                            <p className="text-sm font-bold text-indigo-700 mb-2">{t('appointments.request.acceptedQuote')}</p>
                            <div className="bg-white rounded-xl p-3">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="font-bold text-sm text-gray-900">{t('appointments.request.quote')}{a.quoteSnapshot?.quoteNumber ? ` ${a.quoteSnapshot.quoteNumber}` : ''}</p>
                                    <span className="font-extrabold text-green-700 text-sm">{formatMoney(a.quoteSnapshot?.totalTTC, currentLanguage)}</span>
                                </div>
                                <ul className="mt-2 space-y-1 text-sm text-gray-600">
                                    {items.map((i, idx) => <li key={idx} className="flex justify-between gap-3"><span className="truncate">{i.description}</span><span className="whitespace-nowrap">{formatMoney(i.totalTTC, currentLanguage)}</span></li>)}
                                </ul>
                            </div>
                        </div>

                        <InspectionRecap appointment={a} hideQuoteTotal />

                        <div className={card}>
                            <p className="text-sm font-bold text-gray-900 mb-2">{t('appointments.request.prepare')}</p>
                            <label className="flex items-start gap-2 text-sm text-gray-800 py-1">
                                <input type="checkbox" checked={a.remindClient !== false} disabled readOnly className="mt-1 rounded border-gray-300 text-indigo-600" />
                                <span>{t('appointments.request.remindClient')}</span>
                            </label>
                            <label className="flex items-start gap-2 text-sm text-gray-800 py-1">
                                <input type="checkbox" checked={buffer} onChange={e => setBuffer(e.target.checked)} className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                <span>{t('appointments.request.buffer')}</span>
                            </label>
                        </div>

                        {counterLeft && suggestions.length > 0 && (
                            <div className="rounded-2xl border border-dashed border-gray-300 p-4">
                                <p className="text-sm font-bold text-gray-900 mb-2">{t('appointments.request.proposeInstead')}</p>
                                <div className="flex flex-wrap gap-2">
                                    {suggestions.map(sg => {
                                        const on = picked?.date === sg.date && picked?.timeSlot === sg.timeSlot;
                                        return (
                                            <button key={`${sg.date}-${sg.timeSlot}`} onClick={() => { setPicked(on ? null : sg); setMode('view'); }}
                                                    className={`px-3 py-1.5 rounded-lg border text-sm font-semibold ${on ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50'}`}>
                                                {formatDay(sg.date, currentLanguage, { weekday: 'short', day: 'numeric' })} · {sg.timeSlot}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {mode === 'counter' && (
                        <div className="lg:col-span-5 rounded-2xl border border-indigo-200 p-4 space-y-3">
                            <h3 className="font-bold text-gray-900">{t('appointments.actions.counter')}</h3>
                            <SlotPicker date={date} value={timeSlot} duration={a.duration} onDateChange={setDate} onChange={ts => { setTimeSlot(ts); setPicked(null); }} />
                        </div>
                    )}
                    {mode === 'refuse' && (
                        <div className="lg:col-span-5 space-y-2">
                            <label className="text-sm font-semibold text-gray-700">{t('appointments.actions.reasonLabel')}</label>
                            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="w-full rounded-xl border border-gray-300 px-3.5 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500" />
                        </div>
                    )}
                    {error && <p role="alert" className="lg:col-span-5 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2 px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 sm:rounded-b-2xl sticky bottom-0">
                    {mode !== 'view' && <button onClick={() => { setMode('view'); setError(''); }} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">{t('appointments.actions.back')}</button>}
                    {mode === 'refuse'
                        ? <button disabled={busy} onClick={refuse} className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60">{t('appointments.actions.refuse')}</button>
                        : (<>
                            <button onClick={() => setMode('refuse')} className="px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-semibold hover:bg-red-50">{t('appointments.actions.refuse')}</button>
                            {counterLeft && <button disabled={busy} onClick={counter} className="px-4 py-2 rounded-lg border border-indigo-300 text-indigo-700 text-sm font-semibold hover:bg-indigo-50 disabled:opacity-60">{mode === 'counter' && !picked && !timeSlot ? t('appointments.actions.counter') : t('appointments.actions.sendProposal')}</button>}
                            <button disabled={busy || conflict} onClick={confirm} className="px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{t('appointments.request.confirmAppointment')}</button>
                        </>)}
                </div>
            </div>
        </div>
    );
};
